import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { useListValidations } from 'src/features/validations/api/useListValidations';
import { validationMessages } from 'src/features/validations/utils/messages';
import { Validation } from 'src/features/validations/utils/types';
import { validationTypeBadgeOptions } from 'src/features/validationTypes/utils/constants';
import { validationTypesMessages } from 'src/features/validationTypes/utils/messages';
import { VALIDATION_TYPE } from 'src/features/validationTypes/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { ENTITY_TYPE } from 'src/features/entity/types';
import React from 'react';

const sortingOptions = { initialSortingState: [{ id: 'created_at', desc: true }] };

const ValidationsTableAccountDashboard = () => {
    const { formatMessage, formatNumber } = useIntl();
    const { accountId } = useParams();
    const { parkingId } = useParking();

    const accessActiveStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const accessEntities = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...(accountId && { validator_account: accountId }),
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [querySearchOptions, querySortingOptions, queryPaginationOptions, accountId, parkingId],
    );

    const { data: validations, isLoading } = useListValidations(payload);

    const columnHelper = createColumnHelper<Validation>();

    const columns = [
        columnHelper.accessor('created_at', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            meta: {
                title: formatMessage(paymentsMessages.timestamp),
            },
        }),
        columnHelper.accessor('type.type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const name = ctx.row.original.type.name;
                return (
                    <div className="flex flex-row gap-2">
                        <div className="whitespace-nowrap">{name}</div>
                        <CellBadge {...ctx} />
                    </div>
                );
            },
            meta: {
                title: formatMessage(validationTypesMessages.validationType),
                badgeOptions: validationTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const validationType: VALIDATION_TYPE = row.original.type.type;
                const validationAmount = row.original.amount ?? 0;

                const typeCentsAmount =
                    validationType === VALIDATION_TYPE.CHARGE &&
                    formatNumber(validationAmount / 100, {
                        style: 'currency',
                        currency: row.original.type.currency,
                    });

                const typeMinutesAmount = validationAmount / 60;

                let displayAmount;
                switch (validationType) {
                    case VALIDATION_TYPE.CHARGE:
                        displayAmount = typeCentsAmount;
                        break;
                    case VALIDATION_TYPE.GUEST:
                        displayAmount = '-';
                        break;
                    default:
                        displayAmount = `${formatMessage(commonMessages.minutes, { value: typeMinutesAmount })}`;
                }

                return displayAmount;
            },
            meta: {
                title: formatMessage(validationTypesMessages.validationTypeAmount),
            },
            minSize: 120,
        }),
        columnHelper.accessor('validated_by.name', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const validateByUser = row.original.validated_by?.name;
                const validatedByCloudUser = row.original.validated_by_cloud_user?.name;

                const validatedBy = validateByUser ? validateByUser : validatedByCloudUser;
                return validatedBy ?? '-';
            },
            meta: {
                title: formatMessage(validationTypesMessages.validatedBy),
            },
        }),
        columnHelper.accessor('active_stay_period.stay.ref', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const periodStayId = ctx.row.original.active_stay_period?.stay.id;
                return periodStayId ? (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/${PATH.VISITORS}/active-stay/dashboard/${periodStayId}`}
                        hasPermissions={accessActiveStays}
                    />
                ) : (
                    '-'
                );
            },
            meta: {
                title: formatMessage(validationMessages.activeStayPeriod),
            },
        }),
        columnHelper.accessor('main_identifier.value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(validationTypesMessages.mainIdentifier),
            },
        }),
        columnHelper.accessor('visitor.owner.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/${PATH.ENTITIES}/${ENTITY_TYPE.PERSON}/${ctx.row.original.visitor?.owner?.id}`}
                        hasPermissions={accessEntities}
                    />
                );
            },
            meta: {
                title: formatMessage(validationTypesMessages.owner),
            },
        }),
        columnHelper.accessor('visitor.main_identifier.value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/${PATH.VISITORS}/visitor/${ctx.row.original.visitor?.id}`}
                        hasPermissions={accessVisitors}
                    />
                );
            },
            meta: {
                title: formatMessage(validationTypesMessages.visitor),
            },
        }),
        columnHelper.accessor('past_stay_period.stay.ref', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const periodStayId = ctx.row.original.past_stay_period?.stay.id;
                return periodStayId ? (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/${PATH.VISITORS}/past-stay/dashboard/${periodStayId}`}
                        hasPermissions={accessPastStays}
                    />
                ) : (
                    '-'
                );
            },
            meta: {
                title: formatMessage(validationMessages.pastStayPeriod),
            },
        }),
    ];

    return (
        <DataTable<Validation, any>
            isLoading={isLoading}
            columns={columns}
            data={validations}
            search={search}
            sorting={sorting}
            pagination={pagination}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(validationMessages.validations),
                entryName: formatMessage(validationMessages.validation).toLowerCase(),
                entryNames: formatMessage(validationMessages.validations).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default ValidationsTableAccountDashboard;
